import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Helmet } from 'react-helmet-async';
import {
  Container,
  Card,
  CardContent,
  Box,
  Alert,
  Divider,
  Typography,
  Button,
  Modal,
  Grid,
  Paper,
  CircularProgress,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateRangePicker } from 'react-date-range';
import { makeStyles } from '@mui/styles';

import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

// components
import BreadCrumb from '../components/breadcrumb/BreadCrumbNavigation';

// sections
import ProfileForm from '../sections/profile/ProfileForm';
import ProfileHeader from '../sections/profile/ProfileHeader';

// services
import { createUserProfile, getUserProfile, upsertUserProfile } from '../services/profileService';
import { fileUpload } from '../services/fileUploadService';
import { checkComponentPermission, isBoscCompany } from '../utils/share';
import { PHONE_REGEX } from '../Constants/RegexConstants';
import Loading from '../layouts/loading';
import { fDate } from '../utils/formatDate';
import ProfileStatsCard from '../sections/profile/ProfileStatsCard';
import ProfileTaskTable from '../sections/profile/ProfileTaskTable';
import ProfileScorecardSection from '../sections/profile/ProfileScorecardSection';
import { getMyTaskFilteredTask } from '../services/taskService';
// ----------------------------------------------------------------------

const useStyles = makeStyles((theme) => ({
  dateRangeModal: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '648px',
    backgroundColor: theme?.palette?.background?.paper,
    boxShadow: theme?.shadows?.[24],
    borderRadius: '10px',
    padding: theme?.spacing(4),
  },
  dateRangeSection: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(3),
    backgroundColor: '#f5f5f5',
    borderRadius: '8px',
  },
  dateRangeBtn: {
    color: 'black',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '15px',
  },
  statsCard: {
    padding: theme.spacing(2),
    textAlign: 'center',
    height: '100%',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    borderRadius: '8px',
    transition: 'transform 0.2s',
    '&:hover': {
      transform: 'translateY(-4px)',
    },
  },
  statsValue: {
    fontSize: '2rem',
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
  },
  statsLabel: {
    fontSize: '0.9rem',
    opacity: 0.9,
  },
}));

const ProfileSchema = Yup.object().shape({
  fullName: Yup.string().min(2, 'Too Short!').required('Full name is required'),
  gender: Yup.string().required('Gender is required'),
  phoneNo: Yup.string()
    .matches(PHONE_REGEX, 'Invalid phone number format')
    .min(10, 'Phone number must be at least 10 digits')
    .max(10, 'Phone number must not exceed 10 characters')
    .required('Phone number is required'),
  dateOfBirth: Yup.date().nullable().typeError('Date of Birth is required').required('Date of Birth is required'),
  jobTitle: Yup.string().min(2, 'Too Short!').required('Job title is required'),
  employmentTitle: Yup.string().min(2, 'Too Short!').required('Employment title is required'),
  joiningDate: Yup.date().typeError('Joining date is required').required('Joining date is required'),
  emergencyContactPersonName: Yup.string().min(2, 'Too Short!').required('Contact person name is required'),
  emergencyContact: Yup.string()
    .matches(PHONE_REGEX, 'Invalid phone number format')
    .min(10, 'Emergency contact must be at least 10 digits')
    .max(10, 'Emergency contact must not exceed 10 characters')
    .required('Emergency contact is required'),
});

export default function ProfilePage() {
  const classes = useStyles();
  const { userId } = useParams();
  const currentUser = useSelector((state) => state?.login?.user || null);
  const currentUserId = currentUser?.id;

  const [profileData, setProfileData] = useState(null);
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState(null);
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [originalImage, setOriginalImage] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  // Check if user can edit this profile
  const canEdit = !userId || userId === currentUserId?.toString() || checkComponentPermission('EDIT_USER');
  // filter state
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [tasks, setTasks] = useState(null);
  const [stats, setStats] = useState({
    totalIssues: 0,
    activeProjects: 0,
    avgIssuesPerTask: 1,
    overdueTasks: 0,
  });

  const formik = useFormik({
    initialValues: {
      fullName: '',
      gender: '',
      phoneNo: '',
      dateOfBirth: null,
      jobTitle: '',
      employmentTitle: '',
      joiningDate: null,
      emergencyContact: '',
      emergencyContactPersonName: '',
      profileImage: '',
      // totalIssues: 0,
      // activeProjects: 0,
      // avgIssuesPerTask: 1,
      // overdueTasks: 0,
      dateRange: {
        selection: {
          startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
          endDate: new Date(),
          key: 'selection',
        },
      },
    },
    validationSchema: ProfileSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const profileDataToSubmit = {
          ...values,
          dateOfBirth: values.dateOfBirth ? fDate(values.dateOfBirth, 'yyyy-MM-dd') : null,
          joiningDate: values.joiningDate ? fDate(values.joiningDate, 'yyyy-MM-dd') : null,
        };

        profileDataToSubmit.profileImage = profileImageFile;

        await submitProfileData(profileDataToSubmit);
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Failed to update profile');
        console.error('Profile update error:', error);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const fetchProfileData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      let response;
      if (userId) {
        response = await getUserProfile(userId);
      } else {
        response = await getUserProfile(currentUserId);
      }
      if (response?.data?.success) {
        setUserData(response?.data?.user);
        if (response?.data?.profile) {
          setProfileData(response.data.profile);

          // Update form values with dayjs objects for DatePicker
          const formValues = response.data.profile;
          formik.resetForm({ values: formValues });
        } else {
          const formValues = { ...formik.initialValues };
          formValues.fullName = response.data.user.userName;
          // formValues.totalIssues = response.data.user.totalIssues;
          // formValues.activeProjects = response.data.user.activeProjects;
          // formValues.avgIssuesPerTask = response.data.user.avgIssuesPerTask;
          // formValues.overdueTasks = response.data.user.overdueTasks;
          setStats({
            totalIssues: response.data.user.totalIssues,
            activeProjects: response.data.user.activeProjects,
            avgIssuesPerTask: response.data.user.avgIssuesPerTask,
            overdueTasks: response.data.user.overdueTasks,
          });

          formik.resetForm({ values: formValues });
        }
      } else {
        setError(response?.message || 'Failed to fetch profile');
      }
    } catch (error) {
      setError(error?.response?.data?.message || 'Failed to fetch profile');
      console.error('Profile fetch error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProfileData();
    // Fetch initial statistics with default date range
    const { startDate, endDate } = formik.initialValues.dateRange.selection;
    fetchStatistics(startDate, endDate);
  }, [userId]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    if (originalImage) {
      profileData.profileImage = originalImage;
      setOriginalImage(null);
    }
    setIsEditing(false);
    setProfileImageFile(null);
    setProfileImagePreview(null);

    if (profileData) {
      const formValues = profileData;
      formik.resetForm({ values: formValues });
    } else {
      formik.resetForm({ values: formik.initialValues });
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      formik.setSubmitting(true);
      if (!file.type.startsWith('image/')) {
        toast.error('Please select a valid image file (JPEG, PNG, GIF, etc.)');
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        return;
      }

      if (file.name.length > 100) {
        toast.error('File name is too long. Please rename the file.');
        return;
      }

      const response = await fileUpload('profile_image', file.name, file);

      if (response && response.key && response.getUrl) {
        setProfileImageFile(response.key);
        setProfileImagePreview(response.getUrl);
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('Image upload error:', error);
      toast.error(error?.response?.data?.message || 'Failed to upload image. Please try again.');
      event.target.value = '';
    } finally {
      formik.setSubmitting(false);
    }
  };

  const removeImage = () => {
    try {
      setProfileImageFile(null);
      setOriginalImage(profileData?.profileImage || null);
      if (profileData) {
        profileData.profileImage = '';
      }
      setProfileImagePreview(null);
      formik.setFieldValue('profileImage', '');
      toast.success('Profile image removed successfully');
    } catch (error) {
      console.log(error.message);
      console.error('Error removing image:', error);
      toast.error('Failed to remove image. Please try again.');
    }
  };

  const submitProfileData = async (profileDataToSubmit) => {
    try {
      if (profileDataToSubmit.profileImage === '' || profileDataToSubmit.profileImage === null) {
        if (profileData?.profileImage) {
          delete profileDataToSubmit.profileImage;
        }
      }

      let response;
      if (userId) {
        if (profileData) {
          response = await upsertUserProfile(userId, profileDataToSubmit);
        } else {
          response = await createUserProfile(userId, profileDataToSubmit);
        }
      } else if (profileData) {
        response = await upsertUserProfile(currentUserId, profileDataToSubmit);
      } else {
        response = await createUserProfile(currentUserId, profileDataToSubmit);
      }

      if (response) {
        toast.success('Profile updated successfully');
        setIsEditing(false);
        setProfileImageFile(null);
        setProfileImagePreview(null);
        setOriginalImage(null);
        fetchProfileData();
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update profile');
      console.error('Profile update error:', error);
    }
  };

  const fetchStatistics = async (startDate, endDate) => {
    setIsLoadingStats(true);
    try {
      const targetUserId = userId || currentUserId;
      const formattedStartDate = fDate(startDate, 'yyyy-MM-dd');
      const formattedEndDate = fDate(endDate, 'yyyy-MM-dd');

      const response = await getUserProfile(targetUserId, formattedStartDate, formattedEndDate);

      if (response?.data?.success) {
        // Update statistics in formik (check both statistics and user object for backward compatibility)
        const stats = response.data.statistics || response.data.user;
        if (stats) {
          formik.setFieldValue('totalIssues', stats.totalIssues || 0);
          formik.setFieldValue('overdueTasks', stats.overdueTasks || 0);
          formik.setFieldValue('avgIssuesPerTask', stats.avgIssuesPerTask || 0);
        }
        toast.success('Statistics updated successfully');
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
      toast.error(error?.response?.data?.message || 'Failed to fetch statistics');
    } finally {
      setIsLoadingStats(false);
    }
  };

  const openDateRangePicker = () => setShowDatePicker(true);

  const closeDateRangePicker = () => {
    setShowDatePicker(false);
    // Fetch statistics when date range is applied
    const { startDate, endDate } = formik.values.dateRange.selection;
    fetchStatistics(startDate, endDate);
  };

  const handleDateRangeChange = (ranges) => {
    const prev = formik?.values?.dateRange;
    const newRange = { ...prev, ...ranges };
    formik.setFieldValue('dateRange', newRange);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  const selectedDateRangeText = `${formatDate(formik?.values?.dateRange?.selection?.startDate)} - ${formatDate(
    formik?.values?.dateRange?.selection?.endDate
  )}`;

  // Calculate statistics
  // const totalIssuesGenerated = formik.values.totalIssues || 0;

  // const totalTasksOverdue = formik.values.overdueTasks || 0;

  // const avgIssuesPerTask = formik.values.avgIssuesPerTask || 0;

  // const activeProjects = formik.values.activeProjects || 0;

  const totalIssuesGenerated = stats.totalIssues || 0;
  const totalTasksOverdue = stats.overdueTasks || 0;
  const avgIssuesPerTask = stats.avgIssuesPerTask || 0;
  const activeProjects = stats.activeProjects || 0;

  const breadcrumbs = [
    { label: 'Home', path: '/' },
    { label: userId ? 'Users' : 'Profile', path: userId ? '/users' : '/profile' },
    { label: userId ? `${userData?.userName || 'User'} Profile` : 'My Profile', path: '' },
  ];

  if (isLoading) {
    return <Loading open={isLoading} message="Loading profile..." variant="default" />;
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <BreadCrumb breadcrumbs={breadcrumbs} />
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Helmet>
        <title>{userId ? `${userData?.userName || 'User'} Profile` : 'My Profile'}</title>
      </Helmet>

      <Container maxWidth="lg">
        <BreadCrumb breadcrumbs={breadcrumbs} />

        <Box sx={{ mt: 1 }}>
          <Card elevation={0} sx={{ backgroundColor: 'transparent', boxShadow: 'none' }}>
            <CardContent sx={{ padding: 0 }}>
              <ProfileHeader
                userId={userId}
                userData={userData}
                isEditing={isEditing}
                canEdit={canEdit}
                isSubmitting={formik.isSubmitting}
                onEdit={handleEdit}
                onCancel={handleCancel}
                onSubmit={formik.handleSubmit}
              />

              <Divider sx={{ mb: 3 }} />

              <ProfileForm
                formik={formik}
                userId={userId}
                userData={userData}
                profileImagePreview={profileImagePreview}
                profileImage={formik.values.profileImage}
                isEditing={isEditing}
                canEdit={canEdit}
                isSubmitting={formik.isSubmitting}
                onImageUpload={handleImageUpload}
                onRemoveImage={removeImage}
              />

              <Box className={classes.dateRangeSection}>
                <Typography variant="h6" gutterBottom>
                  Active Projects
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
                  Total active projects (not affected by date range)
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Paper
                      className={classes.statsCard}
                      elevation={3}
                      sx={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' }}
                    >
                      <Typography className={classes.statsValue}>{activeProjects}</Typography>
                      <Typography className={classes.statsLabel}>Active Projects</Typography>
                    </Paper>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 4 }} />

                <Typography variant="h6" gutterBottom>
                  Select Date Range
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2, mb: 3 }}>
                  <Typography variant="body1">Date Range:</Typography>
                  <Button onClick={openDateRangePicker} className={classes.dateRangeBtn} variant="outlined">
                    {selectedDateRangeText}
                  </Button>
                </Box>

                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  Statistics Overview
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
                  Based on selected date range
                </Typography>
                {isLoadingStats ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                    <CircularProgress />
                    <Typography sx={{ mt: 2 }}>Loading statistics...</Typography>
                  </Box>
                ) : (
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Paper className={classes.statsCard} elevation={3}>
                        <Typography className={classes.statsValue}>{totalIssuesGenerated}</Typography>
                        <Typography className={classes.statsLabel}>Issues Generated</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Paper
                        className={classes.statsCard}
                        elevation={3}
                        sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}
                      >
                        <Typography className={classes.statsValue}>{totalTasksOverdue}</Typography>
                        <Typography className={classes.statsLabel}>Tasks Overdue</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Paper
                        className={classes.statsCard}
                        elevation={3}
                        sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}
                      >
                        <Typography className={classes.statsValue}>{avgIssuesPerTask}</Typography>
                        <Typography className={classes.statsLabel}>Avg Issues Per Task</Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                )}
              </Box>

              {isBoscCompany() && (userId ? userData?.userRoleId : currentUser?.userRoleId) === 3 && (
                <ProfileScorecardSection
                  dateRange={formik.values.dateRange}
                  targetUserId={Number(userId || currentUserId)}
                />
              )}
            </CardContent>
            {/* <CardContent>
              <ProfileStatsCard
                stats={{
                  tasks: {
                    NotStarted: formik.values.tasks?.notStarted,
                    Pending: formik.values.tasks?.inProgress,
                    Overdue: formik.values.tasks?.overdue,
                    Completed: formik.values.tasks?.completed,
                    Reopened: formik.values.tasks?.reopened,
                  },
                  cr: {
                    NotStarted: formik.values.cr?.notStarted,
                    Pending: formik.values.cr?.inProgress,
                    Overdue: formik.values.cr?.overdue,
                    Completed: formik.values.cr?.completed,
                    Reopened: formik.values.cr?.reopened,
                  },
                  issues: {
                    NotStarted: formik.values.issues?.notStarted,
                    Pending: formik.values.issues?.inProgress,
                    Overdue: formik.values.issues?.overdue,
                    Completed: formik.values.issues?.completed,
                    Reopened: formik.values.issues?.reopened,
                  },
                }}
                onSelect={(data) => setSelectedFilter(data)}
              />
            </CardContent>
            {selectedFilter && (
              <Box mt={3}>
                <ProfileTaskTable
                  userId={userId || currentUserId}
                  type={selectedFilter.type}
                  status={selectedFilter.status === "pending" ? "inProgress" : selectedFilter.status}
                />
              </Box>
            )} */}
          </Card>
        </Box>

        <Modal open={showDatePicker} onClose={closeDateRangePicker}>
          <Box className={classes.dateRangeModal}>
            <DateRangePicker
              onChange={handleDateRangeChange}
              months={1}
              direction="vertical"
              scroll={{ enabled: true }}
              ranges={[formik?.values?.dateRange?.selection]}
              maxDate={new Date()}
            />
            <Button variant="contained" sx={{ marginTop: '-40px' }} onClick={closeDateRangePicker}>
              Apply
            </Button>
          </Box>
        </Modal>
      </Container>
    </LocalizationProvider>
  );
}
